import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getCitySuggestions, searchCity } from '../../services/sinirService';

const SearchContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const SuggestionItem = styled.li`
  padding: 10px 15px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const ResultSection = styled.div`
  margin-bottom: 20px;
  
  h3 {
    color: #2E7D32;
    margin-bottom: 10px;
    border-bottom: 2px solid #4CAF50;
    padding-bottom: 5px;
  }
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 10px;
`;

const DataItem = styled.div`
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  
  .label {
    font-weight: bold;
    color: #666;
    font-size: 0.9em;
  }
  
  .value {
    color: #333;
    margin-top: 5px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
  
  &.primary {
    background-color: #4CAF50;
    color: white;
    &:hover {
      background-color: #45a049;
    }
  }
  
  &.secondary {
    background-color: #f44336;
    color: white;
    &:hover {
      background-color: #da190b;
    }
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const ErrorMessage = styled.div`
  color: #f44336;
  padding: 10px;
  margin-top: 10px;
  border: 1px solid #f44336;
  border-radius: 4px;
  background-color: #ffebee;
`;

function CitySearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (value) => {
    try {
      const results = await getCitySuggestions(value);
      setSuggestions(results);
      setShowSuggestions(true);
      setError('');
    } catch (err) {
      console.error('Erro ao buscar sugestões:', err);
      setError('Erro ao buscar municípios');
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 2) {
      fetchSuggestions(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (city) => {
    setSearchTerm(city.nome);
    setShowSuggestions(false);
    setLoading(true);
    setError('');

    try {
      const data = await searchCity(city.id);
      setSelectedCity(data);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao buscar dados do município');
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = () => {
    setSelectedCity(null);
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setError('');
  };

  return (
    <SearchContainer ref={searchContainerRef}>
      <h2>Consulta SINIR</h2>
      
      <SearchBox>
        <SearchInput
          type="text"
          placeholder="Digite o nome do município..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
        />
        
        {showSuggestions && suggestions.length > 0 && (
          <SuggestionsList>
            {suggestions.map((city) => (
              <SuggestionItem
                key={city.id}
                onClick={() => handleSuggestionClick(city)}
              >
                {city.nome} - {city.estado}
              </SuggestionItem>
            ))}
          </SuggestionsList>
        )}
      </SearchBox>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && <LoadingSpinner>Carregando dados...</LoadingSpinner>}

      {selectedCity && (
        <ResultCard>
          <h2>{selectedCity.nome} - {selectedCity.estado}</h2>
          
          <ResultSection>
            <h3>Dados Gerais</h3>
            <DataGrid>
              <DataItem>
                <div className="label">População</div>
                <div className="value">{selectedCity.dados.populacao}</div>
              </DataItem>
            </DataGrid>
          </ResultSection>

          <ResultSection>
            <h3>Resíduos Sólidos</h3>
            <DataGrid>
              <DataItem>
                <div className="label">Geração Total</div>
                <div className="value">{selectedCity.dados.residuos_solidos.geracao_total}</div>
              </DataItem>
              <DataItem>
                <div className="label">Coleta Regular</div>
                <div className="value">{selectedCity.dados.residuos_solidos.coleta_regular}</div>
              </DataItem>
              <DataItem>
                <div className="label">Coleta Seletiva</div>
                <div className="value">{selectedCity.dados.residuos_solidos.coleta_seletiva}</div>
              </DataItem>
              <DataItem>
                <div className="label">Reciclagem</div>
                <div className="value">{selectedCity.dados.residuos_solidos.reciclagem}</div>
              </DataItem>
            </DataGrid>
          </ResultSection>

          <ResultSection>
            <h3>Disposição Final</h3>
            <DataGrid>
              <DataItem>
                <div className="label">Aterro Sanitário</div>
                <div className="value">{selectedCity.dados.residuos_solidos.disposicao_final.aterro_sanitario}</div>
              </DataItem>
              <DataItem>
                <div className="label">Aterro Controlado</div>
                <div className="value">{selectedCity.dados.residuos_solidos.disposicao_final.aterro_controlado}</div>
              </DataItem>
              <DataItem>
                <div className="label">Lixão</div>
                <div className="value">{selectedCity.dados.residuos_solidos.disposicao_final.lixao}</div>
              </DataItem>
            </DataGrid>
          </ResultSection>

          <ResultSection>
            <h3>Gestão de Resíduos</h3>
            <DataGrid>
              <DataItem>
                <div className="label">Plano Municipal</div>
                <div className="value">{selectedCity.dados.gestao_residuos.plano_municipal}</div>
              </DataItem>
              <DataItem>
                <div className="label">Cooperativas</div>
                <div className="value">{selectedCity.dados.gestao_residuos.cooperativas}</div>
              </DataItem>
              <DataItem>
                <div className="label">Programa de Compostagem</div>
                <div className="value">{selectedCity.dados.gestao_residuos.programa_compostagem}</div>
              </DataItem>
            </DataGrid>
          </ResultSection>

          <ResultSection>
            <h3>Indicadores Econômicos</h3>
            <DataGrid>
              <DataItem>
                <div className="label">Custo de Coleta</div>
                <div className="value">{selectedCity.dados.indicadores_economicos.custo_coleta}</div>
              </DataItem>
              <DataItem>
                <div className="label">Receita com Reciclagem</div>
                <div className="value">{selectedCity.dados.indicadores_economicos.receita_reciclagem}</div>
              </DataItem>
              <DataItem>
                <div className="label">Investimento em Gestão</div>
                <div className="value">{selectedCity.dados.indicadores_economicos.investimento_gestao}</div>
              </DataItem>
            </DataGrid>
          </ResultSection>

          <ButtonContainer>
            <Button className="secondary" onClick={handleClearData}>
              Limpar Dados
            </Button>
          </ButtonContainer>
        </ResultCard>
      )}
    </SearchContainer>
  );
}

export default CitySearch;
